// /app/brands/new — Onboarding wizard.
//
// Single-page progressive disclosure per design/Flows.html Flow 01:
//   "Operator pastes a URL or uploads a PDF. AI extracts colors,
//   fonts, voice, topics. Operator confirms, connects at least one
//   social account, lands at 'ready to generate'."
//
// V0.2 scope (this session): free-text-description path only.
// Backend already has POST /v1/brands/{id}/ai-onboard which takes
// a paragraph + returns structured suggestions. We use it as the
// "extraction" surface for now. URL-paste with HTML crawling is
// a follow-up (needs headless extraction work).
//
// Visual treatment ported from design-handoff/src/pages/onboarding.jsx:
//   inline style={{}} + var(--*) CSS vars + tokens.css utility classes
//   (no Tailwind). Step indicator on top, two-col Step 1 (intake + "what
//   we extract" sidebar), Rosette + checklist on Step 2, two-col Step 3
//   (review left, live preview card right), big check + CTAs on Step 4.
//
// Steps:
//   1. intake      — name + paragraph + language + country  (+ "what we extract" sidebar)
//   2. extracting  — POST /brands then POST /ai-onboard, animated checklist
//   3. review      — operator deselects bad rows, hits Save; live sample-post preview on right
//   4. done        — big circular check + Open cockpit / Generate first post

import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useActiveBrand } from "@/stores/active-brand";
import type { Brand as FullBrand } from "@/lib/types";
import { api, ApiError } from "@/lib/api";
import { LANGUAGES, COUNTRIES } from "@/lib/geo";
import { Icons, Rosette } from "@/components/icons";
import { PageHead, hexToRgba } from "@/components/primitives";

export const Route = createFileRoute("/app/brands/new")({
  component: NewBrandWizard,
});

// ── State machine ──────────────────────────────────────────────────────────

type Step = "intake" | "extracting" | "review" | "done";

interface IntakeForm {
  trade_name: string;
  description: string;
  language: string; // ISO 639-1 code (see LANGUAGES). Backend expands to BCP-47.
  country: string; // ISO 3166-1 alpha-2 (see COUNTRIES).
}

// Mirrors what ai-onboard returns (subset — full shape in onboard.go).
interface OnboardSuggestion {
  industry?: string;
  category?: string;
  voice_summary?: string;
  voice_attributes?: Array<{ attribute: string; value: number }>;
  demographics?: Record<string, unknown>;
  psychographics?: Record<string, unknown>;
  products?: Array<{ name: string; description?: string; category?: string; features?: string[] }>;
  services?: Array<{ name: string; description?: string; target_audience?: string }>;
  topics_include?: string[];
  topics_avoid?: string[];
  warnings?: string[];
}

interface Brand {
  id: string;
  trade_name: string;
}

interface AcceptedState {
  voice: boolean;
  audience: boolean;
  topicsInclude: boolean;
  topicsAvoid: boolean;
  products: Set<number>;
  services: Set<number>;
}

// Files the operator attached during intake. We keep them in component
// state until the brand row exists (you need an ID to upload assets to),
// then process them inside startExtraction.mutationFn.
type StagedKind =
  | "logo"
  | "example"
  | "product"
  | "background"
  | "team"
  | "document"
  | "deck"     // HTML brand kits → /kit/extract (palette+typography+voice)
  | "kb-file"; // PDF/text knowledge sources → /kb/extract-file (industry/voice/products/services/topics)

interface StagedFile {
  id: string; // local-only, for React key + remove
  file: File;
  kind: StagedKind;
}

function defaultKindFor(file: File): StagedKind {
  const t = file.type || "";
  const n = file.name.toLowerCase();
  // HTML → brand-kit file routed through /kit/extract (palette+voice).
  if (t === "text/html" || n.endsWith(".html") || n.endsWith(".htm")) return "deck";
  // PDFs / plain text / markdown → /kb/extract-file: text is pulled out
  // and run through the same Claude prompt as ai-onboard so the operator
  // gets industry / voice / products / services / topics suggestions.
  if (
    t === "application/pdf" ||
    n.endsWith(".pdf") ||
    n.endsWith(".txt") ||
    n.endsWith(".md")
  ) {
    return "kb-file";
  }
  // All images go up as "example" — backend Haiku vision then sets
  // DetectedKind (logo/product/team/lifestyle/screenshot/text-heavy)
  // and the cockpit reads that for filtering. The operator can also
  // re-tag from the Assets tab if needed.
  if (t.startsWith("image/")) return "example";
  return "document";
}

function kindLabel(k: StagedKind, tr: boolean): string {
  if (tr) {
    return {
      logo: "logo olarak yüklendi",
      example: "örnek post olarak yüklendi",
      product: "ürün görseli olarak yüklendi",
      background: "arka plan olarak yüklendi",
      team: "ekip fotoğrafı olarak yüklendi",
      document: "doküman olarak yüklendi",
      deck: "markakit dosyası",
      "kb-file": "bilgi tabanı için okundu",
    }[k];
  }
  return {
    logo: "uploaded as logo",
    example: "uploaded as example post",
    product: "uploaded as product image",
    background: "uploaded as background",
    team: "uploaded as team photo",
    document: "uploaded as document",
    deck: "brand-kit file",
    "kb-file": "read for knowledge base",
  }[k];
}

// Subset of /kit/extract response we read in the wizard.
type KitExtract = {
  palette?: { hex: string; name?: string; role?: string }[];
  typography?: { display_font?: string; body_font?: string };
  voice_summary?: string;
  logos?: { url?: string }[];
  warnings?: string[];
};

const NEXUS_RED = "#E02D1C";

// ── Component ──────────────────────────────────────────────────────────────

function NewBrandWizard() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { setActive } = useActiveBrand();
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const [step, setStep] = useState<Step>("intake");
  // 402 brand_limit_reached from POST /brands — plan cap hit. Renders the
  // Upgrade panel instead of the wizard. v0.5.71 backend gate.
  const [limitBlock, setLimitBlock] = useState<{
    message?: string; plan_slug?: string; max_brands?: number; brand_count?: number;
  } | null>(null);
  const [form, setForm] = useState<IntakeForm>({
    trade_name: "",
    description: "",
    language: "tr",
    country: "TR",
  });
  const [brand, setBrand] = useState<Brand | null>(null);
  const [suggestion, setSuggestion] = useState<OnboardSuggestion | null>(null);
  const [staged, setStaged] = useState<StagedFile[]>([]);
  // Notes per-file written to review summary after upload — e.g.
  // "logo.png uploaded" / "deck.html — palette + voice extracted".
  const [stagedResults, setStagedResults] = useState<string[]>([]);
  // What the operator has accepted from the suggestion list.
  const [accepted, setAccepted] = useState<AcceptedState>({
    voice: true,
    audience: true,
    topicsInclude: true,
    topicsAvoid: true,
    products: new Set(),
    services: new Set(),
  });

  // ── Step 1 → 2: create brand + kick off AI onboard
  const startExtraction = useMutation({
    mutationFn: async () => {
      // 1. Create the brand (minimal — name + language + country).
      const b = await api<Brand>("/brands", {
        method: "POST",
        body: {
          trade_name: form.trade_name.trim(),
          primary_language: form.language,
          country: form.country,
        },
      });
      // 2. Process staged files in parallel with ai-onboard — assets
      //    upload (kind="logo"/"example"/etc.) goes straight to the
      //    brand's asset library; HTML "deck" files go through
      //    /kit/extract to seed palette/voice from the upload; PDF /
      //    text "kb-file" goes through /kb/extract-file to seed
      //    industry / voice / products / services / topics suggestions.
      const notes: string[] = [];
      const extractedKits: KitExtract[] = [];
      const kbSuggestions: OnboardSuggestion[] = [];

      const fileTasks = staged.map(async (sf) => {
        try {
          if (sf.kind === "deck") {
            const fd = new FormData();
            fd.append("file", sf.file);
            const r = await api<KitExtract>(`/brands/${b.id}/kit/extract`, {
              method: "POST",
              body: fd,
            });
            extractedKits.push(r);
            const parts: string[] = [];
            if (r.palette?.length) parts.push(`${r.palette.length} ${tr ? "renk" : "colors"}`);
            if (r.typography?.display_font || r.typography?.body_font) parts.push(tr ? "font" : "fonts");
            if (r.voice_summary) parts.push(tr ? "ses tonu" : "voice");
            if (r.logos?.length) parts.push(`${r.logos.length} ${tr ? "logo" : "logos"}`);
            notes.push(
              `${sf.file.name} — ${parts.length ? parts.join(" · ") : tr ? "okundu" : "parsed"}`,
            );
          } else if (sf.kind === "kb-file") {
            const fd = new FormData();
            fd.append("file", sf.file);
            const r = await api<OnboardSuggestion>(
              `/brands/${b.id}/kb/extract-file`,
              { method: "POST", body: fd },
            );
            kbSuggestions.push(r);
            const parts: string[] = [];
            if (r.industry) parts.push(tr ? "sektör" : "industry");
            if (r.voice_summary) parts.push(tr ? "ses tonu" : "voice");
            if (r.products?.length) parts.push(`${r.products.length} ${tr ? "ürün" : "products"}`);
            if (r.services?.length) parts.push(`${r.services.length} ${tr ? "hizmet" : "services"}`);
            if (r.topics_include?.length) parts.push(`${r.topics_include.length} ${tr ? "konu" : "topics"}`);
            notes.push(
              `${sf.file.name} — ${parts.length ? parts.join(" · ") : tr ? "okundu" : "parsed"}`,
            );
          } else {
            const fd = new FormData();
            fd.append("kind", sf.kind);
            fd.append("file", sf.file);
            await api(`/brands/${b.id}/assets`, { method: "POST", body: fd });
            notes.push(`${sf.file.name} — ${kindLabel(sf.kind, tr)}`);
          }
        } catch (err) {
          notes.push(
            `${sf.file.name} — ${tr ? "yüklenemedi" : "upload failed"}: ${
              err instanceof ApiError ? err.message : String(err)
            }`,
          );
        }
      });

      // 3. Run ai-onboard against the seed paragraph (in parallel with uploads).
      //    BEST-EFFORT: the brand is already created above, so an ai-onboard
      //    failure (e.g. the Claude call timing out) must NOT fail the whole
      //    wizard — otherwise the operator sees a 500, the brand still exists,
      //    and a retry hits the brand-cap. On failure we fall back to an empty
      //    suggestion and let them fill the review step manually.
      const sugPromise = api<OnboardSuggestion>(`/brands/${b.id}/ai-onboard`, {
        method: "POST",
        body: {
          description: form.description.trim(),
          brand_name: form.trade_name.trim(),
          country: form.country,
          language: form.language,
        },
      }).catch((e: unknown) => {
        notes.push(
          tr
            ? "Yapay zeka önerileri alınamadı — bilgileri sonraki adımda elle girebilirsin."
            : "AI suggestions unavailable — you can fill the details in manually on the next step.",
        );
        void e;
        return {} as OnboardSuggestion;
      });
      const [sug] = await Promise.all([sugPromise, Promise.allSettled(fileTasks)]);

      // Merge PDF/text-derived KB suggestions into the paragraph-derived
      // ones so the review step is one consolidated list. We append-merge
      // products/services/topics (dedupe by name/text), and only adopt
      // industry/voice from the doc if the paragraph didn't give us one
      // (paragraph is the "primary source of truth" by intent).
      for (const k of kbSuggestions) {
        if (!sug.industry && k.industry) sug.industry = k.industry;
        if (!sug.category && k.category) sug.category = k.category;
        if (!sug.voice_summary && k.voice_summary) sug.voice_summary = k.voice_summary;
        if (k.products?.length) {
          const seen = new Set(
            (sug.products ?? []).map((p) => p.name.trim().toLowerCase()),
          );
          for (const p of k.products) {
            const key = p.name.trim().toLowerCase();
            if (!seen.has(key)) {
              sug.products = [...(sug.products ?? []), p];
              seen.add(key);
            }
          }
        }
        if (k.services?.length) {
          const seen = new Set(
            (sug.services ?? []).map((s) => s.name.trim().toLowerCase()),
          );
          for (const s of k.services) {
            const key = s.name.trim().toLowerCase();
            if (!seen.has(key)) {
              sug.services = [...(sug.services ?? []), s];
              seen.add(key);
            }
          }
        }
        if (k.topics_include?.length) {
          const seen = new Set(
            (sug.topics_include ?? []).map((t) => t.toLowerCase()),
          );
          for (const t of k.topics_include) {
            if (!seen.has(t.toLowerCase())) {
              sug.topics_include = [...(sug.topics_include ?? []), t];
              seen.add(t.toLowerCase());
            }
          }
        }
        if (k.topics_avoid?.length) {
          const seen = new Set(
            (sug.topics_avoid ?? []).map((t) => t.toLowerCase()),
          );
          for (const t of k.topics_avoid) {
            if (!seen.has(t.toLowerCase())) {
              sug.topics_avoid = [...(sug.topics_avoid ?? []), t];
              seen.add(t.toLowerCase());
            }
          }
        }
      }

      setStagedResults(notes);

      // 4. If a brand deck was extracted, fold its palette/typography/voice
      //    into the brand kit so the cockpit reflects it. This mirrors
      //    KitTab's "Apply" flow — but for the wizard we auto-apply since
      //    the operator already chose to upload it.
      if (extractedKits.length > 0) {
        const merged = extractedKits[0];
        const kitBody: Record<string, unknown> = {
          // Backend's validKitSource = {uploaded, generated, hybrid}.
          // The wizard uploaded an HTML deck file, so "uploaded" fits.
          source: "uploaded",
          palette: merged.palette ?? [],
          typography: merged.typography ?? {},
        };
        if (merged.voice_summary) kitBody.voice_summary = merged.voice_summary;
        try {
          await api(`/brands/${b.id}/kit`, { method: "PUT", body: kitBody });
        } catch {
          // non-fatal — operator can re-apply from KitTab
        }
      }

      // Default-accept all products + services so the operator deselects rather than selects.
      setAccepted((s) => ({
        ...s,
        products: new Set((sug.products ?? []).map((_, i) => i)),
        services: new Set((sug.services ?? []).map((_, i) => i)),
      }));
      return { brand: b, suggestion: sug };
    },
    onSuccess: ({ brand: b, suggestion: s }) => {
      setBrand(b);
      setSuggestion(s);
      // Switch the active brand to the one just created so the sample-post
      // preview (and --accent) use the NEW brand's theme, not the previously
      // active brand (e.g. Nexus). Also refresh the sidebar brand list so the
      // new brand appears immediately without a page reload.
      // Cast: this wizard's local Brand is minimal (id + trade_name); the
      // store only needs the id now — the invalidation below refetches the
      // full brand and AppShell rebinds --accent from it.
      setActive(b as unknown as FullBrand);
      void qc.invalidateQueries({ queryKey: ["brands"] });
      setStep("review");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 402) {
        setLimitBlock((err.body as typeof limitBlock) ?? {});
      }
      setStep("intake");
    },
  });

  // ── Step 3: apply accepted suggestions to backend
  const applySuggestion = useMutation({
    mutationFn: async () => {
      if (!brand || !suggestion) throw new Error("no brand");
      const tasks: Array<Promise<unknown>> = [];

      // Brand patch — only industry + category (the backend's patchReq
      // accepts those; topics / audience / target_audience are NOT
      // brand columns and have their own endpoints below).
      const brandPatch: Record<string, unknown> = {};
      if (suggestion.industry) brandPatch.industry = suggestion.industry;
      if (suggestion.category) brandPatch.category = suggestion.category;
      if (Object.keys(brandPatch).length > 0) {
        tasks.push(api(`/brands/${brand.id}`, { method: "PATCH", body: brandPatch }));
      }

      // Topics — POST /topics one-by-one. Backend stores each as a row
      // (id + topic + mode), not as arrays on the brand.
      if (accepted.topicsInclude && suggestion.topics_include?.length) {
        for (const t of suggestion.topics_include) {
          tasks.push(
            api(`/brands/${brand.id}/topics`, {
              method: "POST",
              body: { topic: t, mode: "include" },
            }),
          );
        }
      }
      if (accepted.topicsAvoid && suggestion.topics_avoid?.length) {
        for (const t of suggestion.topics_avoid) {
          tasks.push(
            api(`/brands/${brand.id}/topics`, {
              method: "POST",
              body: { topic: t, mode: "avoid" },
            }),
          );
        }
      }

      // Audience → PUT /audience (full upsert, demographics + psychographics).
      if (accepted.audience && (suggestion.demographics || suggestion.psychographics)) {
        const body: Record<string, unknown> = {};
        if (suggestion.demographics) body.demographics = suggestion.demographics;
        if (suggestion.psychographics) body.psychographics = suggestion.psychographics;
        tasks.push(api(`/brands/${brand.id}/audience`, { method: "PUT", body }));
      }

      // Voice — kit voice_summary (PUT /kit). UpsertKit keeps existing
      // palette/typography when fields are omitted, so we only send what
      // we want to update. Source is required.
      if (accepted.voice && suggestion.voice_summary) {
        tasks.push(
          api(`/brands/${brand.id}/kit`, {
            method: "PUT",
            body: {
              source: "generated",
              voice_summary: suggestion.voice_summary,
            },
          }),
        );
      }
      if (accepted.voice && suggestion.voice_attributes?.length) {
        // Voice attrs → PUT /brands/:id/voice (full replacement). Backend
        // accepts a JSON object with attributes[] array.
        tasks.push(
          api(`/brands/${brand.id}/voice`, {
            method: "PUT",
            body: { attributes: suggestion.voice_attributes },
          }),
        );
      }

      // Products
      for (const i of accepted.products) {
        const p = suggestion.products?.[i];
        if (!p) continue;
        tasks.push(api(`/brands/${brand.id}/products`, { method: "POST", body: p }));
      }
      // Services
      for (const i of accepted.services) {
        const s = suggestion.services?.[i];
        if (!s) continue;
        tasks.push(api(`/brands/${brand.id}/services`, { method: "POST", body: s }));
      }
      await Promise.allSettled(tasks);
    },
    onSuccess: () => {
      // Kit/colors just applied — refresh the list so the sidebar + active
      // brand pick up the finalized theme (accent_hex) without a reload.
      void qc.invalidateQueries({ queryKey: ["brands"] });
      setStep("done");
    },
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <PageHead
        eyebrow={tr ? "00 · Yeni marka kur" : "00 · Onboard a new brand"}
        title={
          tr
            ? "Paragraftan üretmeye hazır — 2 dakika."
            : "From paragraph to ready-to-post in 2 minutes."
        }
        subtitle={
          tr
            ? "Markanı bir paragrafta anlat. AI sektörü, ses tonunu, hedef kitleyi ve içerik konularını çıkarır. Sen onaylarsın, üretmeye başlarsın."
            : "Describe your brand in a paragraph. AI extracts industry, voice, audience, and topics. You confirm, then start generating."
        }
      />
      <div
        className="scroll-y"
        style={{ flex: 1, padding: "24px 32px 48px", maxWidth: 1100, margin: "0 auto", width: "100%" }}
      >
        <StepIndicator step={step} tr={tr} />

        {limitBlock ? (
          <div className="card" style={{ padding: 28, maxWidth: 560, margin: "24px auto", textAlign: "center" }}>
            <div style={{ fontSize: 34, marginBottom: 10 }}>🔒</div>
            <h2 style={{ fontSize: 20, marginBottom: 10 }}>
              {tr ? "Marka limitine ulaştın" : "You've reached your brand limit"}
            </h2>
            <p style={{ fontSize: 13, color: "var(--tx2)", lineHeight: 1.6, marginBottom: 18 }}>
              {tr
                ? `Mevcut planın (${limitBlock.plan_slug ?? "trial"}) en fazla ${limitBlock.max_brands ?? 1} marka içeriyor; bu çalışma alanında zaten ${limitBlock.brand_count ?? "-"} marka var. Daha fazla marka eklemek için planını yükselt.`
                : `Your current plan (${limitBlock.plan_slug ?? "trial"}) allows ${limitBlock.max_brands ?? 1} brand(s); this workspace already has ${limitBlock.brand_count ?? "-"}. Upgrade to add more.`}
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button className="btn btn-primary" onClick={() => nav({ to: "/app/billing", search: { tab: "plans" } })}>
                {tr ? "Planları gör ve yükselt" : "See plans & upgrade"}
              </button>
              <button className="btn btn-ghost" onClick={() => nav({ to: "/app" })}>
                {tr ? "Ana sayfa" : "Home"}
              </button>
            </div>
          </div>
        ) : null}
        {!limitBlock && step === "intake" && (
          <IntakeStep
            form={form}
            onChange={setForm}
            staged={staged}
            onStagedChange={setStaged}
            tr={tr}
            onSubmit={() => {
              setStep("extracting");
              startExtraction.mutate();
            }}
            error={startExtraction.error instanceof ApiError ? startExtraction.error.message : null}
          />
        )}

        {step === "extracting" && <ExtractingStep tr={tr} brandName={form.trade_name} />}

        {step === "review" && brand && suggestion && (
          <ReviewStep
            brand={brand}
            suggestion={suggestion}
            accepted={accepted}
            setAccepted={setAccepted}
            saving={applySuggestion.isPending}
            tr={tr}
            error={applySuggestion.error instanceof ApiError ? applySuggestion.error.message : null}
            onApply={() => applySuggestion.mutate()}
            uploadedNotes={stagedResults}
          />
        )}

        {step === "done" && brand && (
          <DoneStep
            brand={brand}
            tr={tr}
            onOpen={() => nav({ to: "/app/brands/$brandId", params: { brandId: brand.id } })}
            onGenerate={() => nav({ to: "/app/generate" })}
          />
        )}
      </div>
    </div>
  );
}

// ── Step indicator (4 numbered circles + connectors) ───────────────────────

function StepIndicator({ step, tr }: { step: Step; tr: boolean }) {
  const steps: Array<{ id: Step; label: string }> = [
    { id: "intake", label: tr ? "Markanı tanıt" : "Describe brand" },
    { id: "extracting", label: tr ? "AI düşünüyor" : "AI extracting" },
    { id: "review", label: tr ? "Gözden geçir" : "Review" },
    { id: "done", label: tr ? "Hazır" : "Done" },
  ];
  const order: Step[] = ["intake", "extracting", "review", "done"];
  const stageIdx = order.indexOf(step);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 0,
        marginBottom: 28,
        flexWrap: "wrap",
      }}
    >
      {steps.map((s, i) => {
        const sIdx = order.indexOf(s.id);
        const done = stageIdx > sIdx;
        const active = stageIdx === sIdx;
        return (
          // Avoid React.Fragment so we don't add another import dependency.
          <div key={s.id} style={{ display: "flex", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: done ? "var(--accent)" : active ? "var(--accent-soft)" : "var(--elev)",
                  border: `1px solid ${done || active ? "var(--accent)" : "var(--b2)"}`,
                  color: done ? "#fff" : active ? "var(--accent)" : "var(--mu)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontFamily: "var(--mono)",
                  fontWeight: 600,
                  transition: "background 250ms, border-color 250ms, color 250ms",
                }}
              >
                {done ? <Icons.Check size={12} /> : i + 1}
              </div>
              <span
                style={{
                  fontSize: 12,
                  color: done || active ? "var(--tx)" : "var(--mu)",
                  whiteSpace: "nowrap",
                }}
              >
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 ? (
              <div
                style={{
                  width: 56,
                  height: 1,
                  background: done ? "var(--accent)" : "var(--b1)",
                  margin: "0 12px",
                }}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// ── Step 1: intake ─────────────────────────────────────────────────────────

function IntakeStep({
  form,
  onChange,
  staged,
  onStagedChange,
  onSubmit,
  error,
  tr,
}: {
  form: IntakeForm;
  onChange: (f: IntakeForm) => void;
  staged: StagedFile[];
  onStagedChange: (next: StagedFile[]) => void;
  onSubmit: () => void;
  error: string | null;
  tr: boolean;
}) {
  const canSubmit = form.trade_name.trim().length >= 2 && form.description.trim().length >= 50;

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const next: StagedFile[] = [...staged];
    for (const f of Array.from(files)) {
      // Skip dupes by name+size (best-effort).
      if (next.some((s) => s.file.name === f.name && s.file.size === f.size)) continue;
      next.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file: f,
        kind: defaultKindFor(f),
      });
    }
    onStagedChange(next);
  };
  const removeStaged = (id: string) => onStagedChange(staged.filter((s) => s.id !== id));

  const extractItems: Array<[string, string, keyof typeof Icons]> = tr
    ? [
        ["Sektör", "Sektör + alt sektör tespiti", "Briefcase"],
        ["Ses tonu", "Tonu özetler + 5 nitelik puanlar", "Mic"],
        ["Hedef kitle", "Demografik + psikografik kesim", "Users"],
        ["Konular", "İçerik için temalar + kaçınılacaklar", "Hash"],
        ["Ürün/Hizmet", "Sayfada anılan teklifler", "Folder"],
        ["Bilgi tabanı", "İlk kayıtlar, sonra zenginleştirilir", "Layers"],
      ]
    : [
        ["Industry", "Industry + sub-category inference", "Briefcase"],
        ["Voice", "Tone summary + 5 voice attributes", "Mic"],
        ["Audience", "Demographic + psychographic cuts", "Users"],
        ["Topics", "Themes to use + topics to avoid", "Hash"],
        ["Products / services", "Offerings mentioned in your text", "Folder"],
        ["Knowledge base", "Seed records, you enrich later", "Layers"],
      ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 24, alignItems: "start" }}>
      {/* Left: form */}
      <div>
        <div className="eyebrow" style={{ marginBottom: 10 }}>
          {tr ? "— Adım 1" : "— Step 1"}
        </div>
        <h2 style={{ fontSize: 26, marginBottom: 6, fontFamily: "var(--display)" }}>
          {tr ? "Markanı tanıt." : "Tell us about the brand."}
        </h2>
        <p style={{ color: "var(--tx2)", lineHeight: 1.7, marginBottom: 22 }}>
          {tr
            ? "Bir paragrafta ne yaptığını, kime hizmet ettiğini, neye değer verdiğini anlat. AI, geri kalan her şeyi senin için bir taslak haline getirir."
            : "In one paragraph, say what you do, who you serve, what you care about. AI drafts the rest for you to confirm."}
        </p>

        <div className="card" style={{ padding: 20, marginBottom: 14 }}>
          <label
            style={{
              display: "block",
              fontSize: 12,
              color: "var(--tx2)",
              marginBottom: 6,
              fontWeight: 500,
            }}
          >
            {tr ? "Marka adı" : "Brand name"}{" "}
            <span style={{ color: "var(--accent)" }}>*</span>
          </label>
          <input
            className="input"
            value={form.trade_name}
            onChange={(e) => onChange({ ...form, trade_name: e.target.value })}
            placeholder={tr ? "Örn. TradeGPT" : "e.g. TradeGPT"}
            autoFocus
          />
        </div>

        <div className="card" style={{ padding: 20, marginBottom: 14 }}>
          <label
            style={{
              display: "block",
              fontSize: 12,
              color: "var(--tx2)",
              marginBottom: 6,
              fontWeight: 500,
            }}
          >
            {tr ? "Markanı anlat" : "Describe your brand"}{" "}
            <span style={{ color: "var(--accent)" }}>*</span>
          </label>
          <textarea
            className="input"
            value={form.description}
            onChange={(e) => onChange({ ...form, description: e.target.value })}
            rows={6}
            style={{ resize: "vertical", fontFamily: "var(--body)", lineHeight: 1.55 }}
            placeholder={
              tr
                ? "Markanız ne yapıyor, kime hitap ediyor ve tarzı nasıl? Birkaç cümleyle anlatın; AI markanızı buradan tanıyacak."
                : "What your brand does, who it serves, and its style — a few sentences. The AI learns your brand from this."
            }
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 8,
              fontSize: 11,
            }}
          >
            <span style={{ color: "var(--mu)" }}>
              {tr
                ? "En az 50 karakter. AI sektör, ses tonu, hedef kitle ve konuları çıkarır."
                : "At least 50 chars. AI infers industry, voice, audience, topics."}
            </span>
            <span
              className="mono"
              style={{
                color: form.description.length >= 50 ? "var(--success)" : "var(--mu)",
                fontSize: 11,
              }}
            >
              {form.description.length} / 50
            </span>
          </div>
        </div>

        {/* Optional: upload documents (HTML brand decks → AI extracts colors,
            fonts, voice) and images (logo / example posts / templates).
            Staged here, processed in the extracting step after the brand
            row is created. */}
        <div className="card" style={{ padding: 20, marginBottom: 14 }}>
          <label
            style={{
              display: "block",
              fontSize: 12,
              color: "var(--tx2)",
              marginBottom: 4,
              fontWeight: 500,
            }}
          >
            {tr ? "Dokümanlar ve görseller" : "Documents and images"}{" "}
            <span style={{ color: "var(--mu)", fontWeight: 400 }}>
              ({tr ? "isteğe bağlı" : "optional"})
            </span>
          </label>
          <p style={{ fontSize: 11, color: "var(--mu)", margin: "0 0 12px", lineHeight: 1.55 }}>
            {tr
              ? "Hepsini buraya bırak — AI dosyaları tek tek kategorilere ayırır (logo, örnek post, ürün vb.). PDF/metin dokümanlardan ürünler, hizmetler ve konular; HTML marka kitlerinden renk, font ve ses tonu çıkarılır."
              : "Drop everything here — AI auto-categorizes each file (logo, example post, product, etc.). PDFs and text docs surface products, services, and topics; HTML brand kits surface colors, fonts, and voice."}
          </p>
          <label
            htmlFor="onboard-files"
            style={{
              display: "block",
              padding: "18px 14px",
              border: "1.5px dashed var(--b2)",
              borderRadius: 8,
              textAlign: "center",
              cursor: "pointer",
              background: "var(--surface)",
              color: "var(--tx2)",
              fontSize: 13,
              transition: "border-color 150ms, background 150ms",
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = "var(--accent)";
              e.currentTarget.style.background = hexToRgba(NEXUS_RED, 0.05);
            }}
            onDragLeave={(e) => {
              e.currentTarget.style.borderColor = "var(--b2)";
              e.currentTarget.style.background = "var(--surface)";
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = "var(--b2)";
              e.currentTarget.style.background = "var(--surface)";
              handleFiles(e.dataTransfer.files);
            }}
          >
            <Icons.Upload size={16} style={{ marginRight: 8, verticalAlign: "middle" }} />
            {tr
              ? "Dosyaları buraya bırak veya tıkla — PNG, JPG, WebP, PDF, HTML, TXT, MD"
              : "Drop files here or click — PNG, JPG, WebP, PDF, HTML, TXT, MD"}
          </label>
          <input
            id="onboard-files"
            type="file"
            multiple
            accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,text/html,text/plain,text/markdown,.html,.htm,.pdf,.txt,.md"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = ""; // allow re-selecting same file
            }}
            style={{ display: "none" }}
          />
          {staged.length > 0 ? (
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
              {staged.map((s) => (
                <div
                  key={s.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px",
                    background: "var(--elev)",
                    borderRadius: 6,
                    border: "1px solid var(--b1)",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--tx)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={s.file.name}
                    >
                      {s.file.name}
                    </div>
                    <div className="mono" style={{ fontSize: 10, color: "var(--mu)" }}>
                      {(s.file.size / 1024).toFixed(0)} KB
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 11,
                      padding: "3px 8px",
                      borderRadius: 999,
                      background:
                        s.kind === "deck" || s.kind === "kb-file"
                          ? hexToRgba("#10B981", 0.12)
                          : s.kind === "document"
                          ? hexToRgba("#6B7280", 0.18)
                          : hexToRgba(NEXUS_RED, 0.1),
                      color:
                        s.kind === "deck" || s.kind === "kb-file"
                          ? "var(--success)"
                          : s.kind === "document"
                          ? "var(--tx2)"
                          : "var(--accent)",
                      whiteSpace: "nowrap",
                    }}
                    title={
                      tr
                        ? s.kind === "deck"
                          ? "HTML marka kiti olarak okunacak — renkler, fontlar, ses tonu çıkarılır"
                          : s.kind === "kb-file"
                          ? "Metin çıkarılır ve AI bilgi tabanı önerileri üretir (sektör, ses tonu, ürünler, hizmetler, konular)"
                          : s.kind === "document"
                          ? "Doküman olarak kütüphaneye eklenir"
                          : "Görsel olarak yüklenir, AI türünü otomatik belirler"
                        : s.kind === "deck"
                        ? "Will be read as an HTML brand kit — colors / fonts / voice extracted"
                        : s.kind === "kb-file"
                        ? "Text is extracted, AI generates knowledge-base suggestions (industry, voice, products, services, topics)"
                        : s.kind === "document"
                        ? "Stored in the library as a document"
                        : "Uploaded as image; AI auto-detects its type"
                    }
                  >
                    {s.kind === "deck"
                      ? tr
                        ? "Marka kiti → AI okur"
                        : "Brand kit → AI reads"
                      : s.kind === "kb-file"
                      ? tr
                        ? "PDF/Metin → AI okur"
                        : "PDF/Text → AI reads"
                      : s.kind === "document"
                      ? tr
                        ? "Doküman"
                        : "Document"
                      : tr
                      ? "Görsel · AI sınıflar"
                      : "Image · AI tags"}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeStaged(s.id)}
                    aria-label={tr ? "Kaldır" : "Remove"}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "var(--mu)",
                      cursor: "pointer",
                      padding: 4,
                      lineHeight: 1,
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div className="card" style={{ padding: 20, marginBottom: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  color: "var(--tx2)",
                  marginBottom: 6,
                  fontWeight: 500,
                }}
              >
                {tr ? "Birincil dil" : "Primary language"}
              </label>
              <select
                className="input"
                value={form.language}
                onChange={(e) => onChange({ ...form, language: e.target.value })}
              >
                {LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  color: "var(--tx2)",
                  marginBottom: 6,
                  fontWeight: 500,
                }}
              >
                {tr ? "Birincil pazar" : "Primary market"}
              </label>
              <select
                className="input"
                value={form.country}
                onChange={(e) => onChange({ ...form, country: e.target.value })}
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {error ? (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: 8,
              background: hexToRgba("#EF4444", 0.1),
              border: `1px solid ${hexToRgba("#EF4444", 0.3)}`,
              color: "var(--error)",
              fontSize: 13,
              marginBottom: 12,
            }}
            role="alert"
          >
            {error}
          </div>
        ) : null}

        <button
          className="btn btn-primary btn-lg"
          onClick={onSubmit}
          disabled={!canSubmit}
          style={{
            width: "100%",
            justifyContent: "center",
            opacity: canSubmit ? 1 : 0.5,
            cursor: canSubmit ? "pointer" : "not-allowed",
          }}
          type="button"
        >
          <Icons.Sparkles size={14} />
          {tr ? "AI'a danış" : "Ask AI"}
          <Icons.ArrowRight size={14} />
        </button>
      </div>

      {/* Right: "What we extract" sidebar */}
      <div className="card" style={{ padding: 24, background: "var(--base)" }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}>
          {tr ? "— Çıkardıklarımız" : "— What we extract"}
        </div>
        {extractItems.map(([title, body, iconKey]) => {
          const Icn = Icons[iconKey];
          return (
            <div
              key={title}
              style={{
                display: "flex",
                gap: 12,
                padding: "10px 0",
                alignItems: "center",
                borderBottom: "1px dashed var(--b1)",
              }}
            >
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 6,
                  background: "var(--elev)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--accent)",
                  flexShrink: 0,
                }}
              >
                <Icn size={14} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: "var(--tx)" }}>{title}</div>
                <div style={{ fontSize: 11, color: "var(--mu)" }}>{body}</div>
              </div>
            </div>
          );
        })}
        <div
          style={{
            marginTop: 14,
            padding: "10px 12px",
            background: "var(--surface)",
            border: "1px solid var(--b2)",
            borderRadius: 8,
            fontSize: 11,
            color: "var(--tx2)",
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            lineHeight: 1.5,
          }}
        >
          <Icons.Info size={13} style={{ color: "var(--accent)", marginTop: 1, flexShrink: 0 }} />
          <span>
            {tr
              ? "Hiçbir şey otomatik kaydedilmez. Bir sonraki adımda her şeyi gözden geçirip seçeceksin."
              : "Nothing is saved automatically. You'll review and select on the next step."}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Step 2: extracting (Rosette + animated checklist) ──────────────────────

function ExtractingStep({ tr, brandName }: { tr: boolean; brandName: string }) {
  const steps = tr
    ? [
        "Paragraf okunuyor…",
        "Sektör ve alt kategori tespit ediliyor…",
        "Ses tonu ve nitelikler çıkarılıyor…",
        "Hedef kitle profilleniyor…",
        "İçerik konuları ve kaçınılacaklar derleniyor…",
      ]
    : [
        "Reading paragraph…",
        "Inferring industry and sub-category…",
        "Extracting voice tone and attributes…",
        "Profiling target audience…",
        "Drafting topics to use + topics to avoid…",
      ];
  const [stepIdx, setStepIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStepIdx((s) => Math.min(s + 1, steps.length - 1)), 700);
    return () => clearInterval(id);
  }, [steps.length]);

  return (
    <div style={{ textAlign: "center", padding: "48px 24px", position: "relative" }}>
      <Rosette size={120} stroke="var(--accent)" spin opacity={0.7} />
      <div className="eyebrow" style={{ marginTop: 28, justifyContent: "center" }}>
        {tr ? `— ${brandName || "Marka"} okunuyor` : `— Reading ${brandName || "brand"}`}
      </div>
      <h2
        style={{
          fontSize: 28,
          marginTop: 12,
          fontFamily: "var(--display)",
          letterSpacing: "-0.02em",
        }}
      >
        {tr ? "AI markanı okuyor…" : "AI is reading the brand…"}
      </h2>
      <div style={{ maxWidth: 440, margin: "24px auto 0", textAlign: "left" }}>
        {steps.map((s, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "8px 0",
              opacity: i > stepIdx ? 0.3 : 1,
              transition: "opacity 250ms",
            }}
          >
            {i < stepIdx ? (
              <Icons.Check size={14} style={{ color: "var(--success)" }} />
            ) : i === stepIdx ? (
              <Rosette size={14} stroke="var(--accent)" spin />
            ) : (
              <div
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  border: "1.5px solid var(--mu)",
                  flexShrink: 0,
                }}
              />
            )}
            <span style={{ fontSize: 13, color: i <= stepIdx ? "var(--tx)" : "var(--mu)" }}>
              {s}
            </span>
          </div>
        ))}
      </div>
      <div
        style={{
          marginTop: 28,
          fontSize: 12,
          color: "var(--mu)",
          maxWidth: 480,
          margin: "28px auto 0",
          lineHeight: 1.7,
        }}
      >
        {tr
          ? "Birkaç saniye sürer. Bu ekran kendiliğinden ilerleyecek — sayfayı kapatma."
          : "Takes a few seconds. This screen advances on its own — don't close the page."}
      </div>
    </div>
  );
}

// ── Step 3: review (2-col layout, suggestion left + live preview right) ────

function ReviewStep({
  brand,
  suggestion,
  accepted,
  setAccepted,
  saving,
  error,
  onApply,
  tr,
  uploadedNotes,
}: {
  brand: Brand;
  suggestion: OnboardSuggestion;
  accepted: AcceptedState;
  setAccepted: (next: AcceptedState) => void;
  saving: boolean;
  error: string | null;
  onApply: () => void;
  tr: boolean;
  uploadedNotes: string[];
}) {
  const a = accepted;
  const set = setAccepted;
  const accent = NEXUS_RED; // Wizard doesn't have an extracted palette yet.

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 20, alignItems: "start" }}>
      {/* Left: review surfaces */}
      <div>
        <div className="eyebrow" style={{ marginBottom: 10 }}>
          {tr ? "— Adım 2" : "— Step 2"}
        </div>
        <h2 style={{ fontSize: 26, marginBottom: 6, fontFamily: "var(--display)" }}>
          {tr ? "İşte bulduklarımız." : "Here's what we got."}
        </h2>
        <p style={{ color: "var(--tx2)", marginBottom: 20, lineHeight: 1.7 }}>
          {tr
            ? "İstediklerini seç. Marka panelinden istediğin zaman düzenleyebilirsin."
            : "Pick what to apply. You can refine anything from the brand cockpit afterward."}
        </p>

        {suggestion.warnings && suggestion.warnings.length > 0 ? (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 8,
              background: hexToRgba("#F59E0B", 0.08),
              border: `1px solid ${hexToRgba("#F59E0B", 0.3)}`,
              marginBottom: 12,
            }}
          >
            <div
              className="mono"
              style={{
                fontSize: 10,
                letterSpacing: "0.16em",
                color: "var(--warning)",
                textTransform: "uppercase",
                marginBottom: 4,
              }}
            >
              {tr ? "Dikkat" : "Heads up"}
            </div>
            <ul style={{ listStyle: "none", padding: 0, fontSize: 12, color: "var(--tx)" }}>
              {suggestion.warnings.map((w, i) => (
                <li key={i} style={{ marginTop: 2 }}>
                  • {w}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {uploadedNotes.length > 0 ? (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 8,
              background: hexToRgba("#10B981", 0.08),
              border: `1px solid ${hexToRgba("#10B981", 0.3)}`,
              marginBottom: 12,
            }}
          >
            <div
              className="mono"
              style={{
                fontSize: 10,
                letterSpacing: "0.16em",
                color: "var(--success)",
                textTransform: "uppercase",
                marginBottom: 6,
              }}
            >
              {tr ? "Yüklenenler" : "Uploads"}
            </div>
            <ul style={{ listStyle: "none", padding: 0, fontSize: 12, color: "var(--tx)" }}>
              {uploadedNotes.map((n, i) => (
                <li key={i} style={{ marginTop: 2 }}>
                  • {n}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {/* Industry / category — auto-applied, preview only */}
        {suggestion.industry || suggestion.category ? (
          <PreviewCard label={tr ? "Sektör" : "Industry"}>
            <div style={{ fontSize: 14, color: "var(--tx)" }}>
              {suggestion.industry}
              {suggestion.category ? ` · ${suggestion.category}` : ""}
            </div>
          </PreviewCard>
        ) : null}

        {/* Voice */}
        {suggestion.voice_summary ? (
          <ToggleCard
            label={tr ? "Marka sesi" : "Brand voice"}
            enabled={a.voice}
            onToggle={() => set({ ...a, voice: !a.voice })}
            tr={tr}
          >
            <div
              style={{
                fontSize: 14,
                fontFamily: "var(--display)",
                fontWeight: 500,
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
              }}
            >
              {suggestion.voice_summary}
            </div>
            {suggestion.voice_attributes && suggestion.voice_attributes.length > 0 ? (
              <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
                {suggestion.voice_attributes.map((v) => (
                  <span key={v.attribute} className="badge badge-gray">
                    {v.attribute}: {v.value.toFixed(1)}
                  </span>
                ))}
              </div>
            ) : null}
          </ToggleCard>
        ) : null}

        {/* Audience */}
        {suggestion.demographics || suggestion.psychographics ? (
          <ToggleCard
            label={tr ? "Hedef kitle" : "Audience"}
            enabled={a.audience}
            onToggle={() => set({ ...a, audience: !a.audience })}
            tr={tr}
          >
            <div style={{ fontSize: 13, color: "var(--tx)", lineHeight: 1.6 }}>
              {buildAudienceText(suggestion.demographics, suggestion.psychographics) ||
                (tr ? "(boş)" : "(empty)")}
            </div>
          </ToggleCard>
        ) : null}

        {/* Topics include */}
        {suggestion.topics_include && suggestion.topics_include.length > 0 ? (
          <ToggleCard
            label={tr ? "Konuşulacak konular" : "Topics to use"}
            enabled={a.topicsInclude}
            onToggle={() => set({ ...a, topicsInclude: !a.topicsInclude })}
            tr={tr}
          >
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {suggestion.topics_include.map((t) => (
                <span key={t} className="badge badge-gray">
                  {t}
                </span>
              ))}
            </div>
          </ToggleCard>
        ) : null}

        {/* Topics avoid */}
        {suggestion.topics_avoid && suggestion.topics_avoid.length > 0 ? (
          <ToggleCard
            label={tr ? "Kaçınılacak konular" : "Topics to avoid"}
            enabled={a.topicsAvoid}
            onToggle={() => set({ ...a, topicsAvoid: !a.topicsAvoid })}
            tr={tr}
          >
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {suggestion.topics_avoid.map((t) => (
                <span key={t} className="badge badge-red">
                  {t}
                </span>
              ))}
            </div>
          </ToggleCard>
        ) : null}

        {/* Products */}
        {suggestion.products && suggestion.products.length > 0 ? (
          <MultiCard label={tr ? "Ürünler" : "Products"}>
            {suggestion.products.map((p, i) => (
              <ItemRow
                key={i}
                title={p.name}
                subtitle={p.description ?? p.category ?? ""}
                enabled={a.products.has(i)}
                onToggle={() => {
                  const next = new Set(a.products);
                  if (next.has(i)) next.delete(i);
                  else next.add(i);
                  set({ ...a, products: next });
                }}
              />
            ))}
          </MultiCard>
        ) : null}

        {/* Services */}
        {suggestion.services && suggestion.services.length > 0 ? (
          <MultiCard label={tr ? "Hizmetler" : "Services"}>
            {suggestion.services.map((s, i) => (
              <ItemRow
                key={i}
                title={s.name}
                subtitle={s.description ?? s.target_audience ?? ""}
                enabled={a.services.has(i)}
                onToggle={() => {
                  const next = new Set(a.services);
                  if (next.has(i)) next.delete(i);
                  else next.add(i);
                  set({ ...a, services: next });
                }}
              />
            ))}
          </MultiCard>
        ) : null}

        {error ? (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: 8,
              background: hexToRgba("#EF4444", 0.1),
              border: `1px solid ${hexToRgba("#EF4444", 0.3)}`,
              color: "var(--error)",
              fontSize: 13,
              marginTop: 12,
            }}
            role="alert"
          >
            {error}
          </div>
        ) : null}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginTop: 22,
          }}
        >
          <button className="btn btn-ghost" onClick={onApply} disabled={saving} type="button">
            {tr ? "Tümünü atla" : "Skip all"}
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="btn btn-primary btn-lg"
            onClick={onApply}
            disabled={saving}
            type="button"
            style={{ opacity: saving ? 0.7 : 1 }}
          >
            {saving ? (
              <Rosette size={14} stroke="#fff" spin />
            ) : (
              <Icons.Check size={14} />
            )}
            {saving
              ? tr
                ? "Uygulanıyor…"
                : "Applying…"
              : tr
                ? "Seçilenleri uygula"
                : "Apply selection"}
            {!saving ? <Icons.ArrowRight size={14} /> : null}
          </button>
        </div>
      </div>

      {/* Right: live preview card */}
      <div
        className="card"
        style={{
          padding: 24,
          background: "var(--base)",
          position: "sticky",
          top: 0,
          height: "fit-content",
        }}
      >
        <div className="eyebrow" style={{ marginBottom: 16 }}>
          {tr ? "— Örnek gönderi · canlı önizleme" : "— Sample post · live preview"}
        </div>
        <div
          style={{
            aspectRatio: "1",
            borderRadius: 12,
            background: `linear-gradient(135deg, ${hexToRgba(accent, 0.31)}, #000 60%, ${hexToRgba(accent, 0.12)})`,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: 24,
            position: "relative",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                background: accent,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "var(--display)",
                fontWeight: 700,
                fontSize: 11,
              }}
            >
              {initialsOf(brand.trade_name)}
            </div>
            <span
              className="mono"
              style={{ fontSize: 9, color: "#fff", opacity: 0.7, letterSpacing: "0.1em" }}
            >
              {tr ? "— ÖNİZLEME" : "— PREVIEW"}
            </span>
          </div>
          <div>
            <div
              style={{
                fontFamily: "var(--display)",
                fontWeight: 800,
                fontSize: 28,
                color: "#fff",
                letterSpacing: "-0.02em",
                lineHeight: 1.1,
                marginBottom: 8,
              }}
            >
              {brand.trade_name}.
            </div>
            <div
              style={{
                fontFamily: "var(--body)",
                fontSize: 13,
                color: "rgba(255,255,255,0.72)",
                lineHeight: 1.5,
              }}
            >
              {previewBlurb(suggestion, tr)}
            </div>
          </div>
        </div>
        <div
          style={{
            marginTop: 18,
            fontSize: 12,
            color: "var(--tx2)",
            lineHeight: 1.7,
          }}
        >
          <b style={{ color: "var(--tx)" }}>
            {suggestion.voice_summary
              ? `"${truncate(suggestion.voice_summary, 140)}"`
              : tr
                ? '"Markanın sesi burada belirir — AI çıkardığı tonu uygular."'
                : '"Your brand voice will appear here — AI applies the extracted tone."'}
          </b>
          <br />
          <span style={{ color: "var(--mu)" }}>
            {tr
              ? `— İlk örnek caption, ${brand.trade_name} sesinde üretildi.`
              : `— First sample caption, generated in ${brand.trade_name}'s voice.`}
          </span>
        </div>

        {suggestion.industry ? (
          <div
            style={{
              marginTop: 14,
              padding: "10px 12px",
              background: "var(--surface)",
              border: "1px solid var(--b2)",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12,
            }}
          >
            <Icons.Briefcase size={13} style={{ color: "var(--accent)" }} />
            <span style={{ color: "var(--tx2)" }}>
              {tr ? "Sektör: " : "Industry: "}
              <span style={{ color: "var(--tx)" }}>{suggestion.industry}</span>
              {suggestion.category ? ` · ${suggestion.category}` : ""}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ── Step 4: done ───────────────────────────────────────────────────────────

function DoneStep({
  brand,
  onOpen,
  onGenerate,
  tr,
}: {
  brand: Brand;
  onOpen: () => void;
  onGenerate: () => void;
  tr: boolean;
}) {
  const accent = NEXUS_RED;
  return (
    <div style={{ textAlign: "center", padding: "48px 24px" }}>
      <div
        style={{
          width: 84,
          height: 84,
          borderRadius: "50%",
          margin: "0 auto 24px",
          background: hexToRgba(accent, 0.2),
          color: accent,
          border: `2px solid ${accent}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icons.Check size={36} />
      </div>
      <div className="eyebrow" style={{ marginBottom: 12, justifyContent: "center" }}>
        {tr ? "— Marka oluşturuldu" : "— Brand created"}
      </div>
      <h2
        style={{
          fontSize: 36,
          marginBottom: 12,
          fontFamily: "var(--display)",
          letterSpacing: "-0.02em",
        }}
      >
        {tr ? `${brand.trade_name} hazır.` : `${brand.trade_name} is ready.`}
      </h2>
      <p
        style={{
          color: "var(--tx2)",
          maxWidth: 480,
          margin: "0 auto 32px",
          lineHeight: 1.7,
        }}
      >
        {tr
          ? "Sesini iş başında görmek için ilk gönderini üret. Model ilk 10 onayından öğrenir — net geri bildirim ver."
          : "Generate a first post to see the voice in action. The model learns from your first 10 approvals — give it real feedback."}
      </p>
      <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
        <button className="btn btn-secondary" onClick={onOpen} type="button">
          {tr ? "Marka panelini aç" : "Open brand cockpit"}
        </button>
        <button className="btn btn-primary btn-lg" onClick={onGenerate} type="button">
          <Icons.Sparkles size={15} />
          {tr ? "İlk gönderiyi üret" : "Generate first post"}
        </button>
      </div>
    </div>
  );
}

// ── Card primitives ────────────────────────────────────────────────────────

function PreviewCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="card" style={{ padding: 18, marginBottom: 12 }}>
      <div
        className="mono"
        style={{
          fontSize: 10,
          color: "var(--mu)",
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function ToggleCard({
  label,
  enabled,
  onToggle,
  tr,
  children,
}: {
  label: string;
  enabled: boolean;
  onToggle: () => void;
  tr: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className="card"
      style={{
        padding: 18,
        marginBottom: 12,
        borderColor: enabled ? "var(--accent)" : "var(--b2)",
        background: enabled ? "var(--surface)" : "var(--surface)",
        transition: "border-color 200ms",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
          gap: 12,
        }}
      >
        <span
          className="mono"
          style={{
            fontSize: 10,
            color: "var(--mu)",
            letterSpacing: "0.16em",
            textTransform: "uppercase",
          }}
        >
          {label}
        </span>
        <button
          onClick={onToggle}
          className={enabled ? "btn btn-primary btn-sm" : "btn btn-secondary btn-sm"}
          type="button"
        >
          {enabled ? <Icons.Check size={11} /> : null}
          {enabled ? (tr ? "Uygula" : "Apply") : tr ? "Atla" : "Skip"}
        </button>
      </div>
      {children}
    </div>
  );
}

function MultiCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="card" style={{ marginBottom: 12, overflow: "hidden" }}>
      <div
        style={{
          padding: "12px 18px",
          borderBottom: "1px solid var(--b1)",
        }}
      >
        <span
          className="mono"
          style={{
            fontSize: 10,
            color: "var(--mu)",
            letterSpacing: "0.16em",
            textTransform: "uppercase",
          }}
        >
          {label}
        </span>
      </div>
      <div>{children}</div>
    </div>
  );
}

function ItemRow({
  title,
  subtitle,
  enabled,
  onToggle,
}: {
  title: string;
  subtitle?: string;
  enabled: boolean;
  onToggle: () => void;
}) {
  return (
    <label
      style={{
        padding: "12px 18px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        borderBottom: "1px solid var(--b1)",
        cursor: "pointer",
      }}
    >
      <input
        type="checkbox"
        checked={enabled}
        onChange={onToggle}
        style={{ width: 16, height: 16, accentColor: "var(--accent)", flexShrink: 0 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--tx)" }}>{title}</div>
        {subtitle ? (
          <div style={{ fontSize: 12, color: "var(--tx2)", marginTop: 2 }}>{subtitle}</div>
        ) : null}
      </div>
    </label>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function buildAudienceText(
  demo: Record<string, unknown> | undefined,
  psycho: Record<string, unknown> | undefined,
): string {
  const parts: string[] = [];
  if (demo) {
    parts.push(
      Object.entries(demo)
        .map(([k, v]) => `${k}: ${formatAny(v)}`)
        .join(", "),
    );
  }
  if (psycho) {
    parts.push(
      Object.entries(psycho)
        .map(([k, v]) => `${k}: ${formatAny(v)}`)
        .join(", "),
    );
  }
  return parts.filter(Boolean).join(" · ");
}

function formatAny(v: unknown): string {
  if (Array.isArray(v)) return v.join(", ");
  if (v && typeof v === "object") return JSON.stringify(v);
  return String(v ?? "");
}

function initialsOf(name: string): string {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1).trimEnd() + "…";
}

function previewBlurb(suggestion: OnboardSuggestion, tr: boolean): string {
  if (suggestion.topics_include && suggestion.topics_include.length > 0) {
    const ts = suggestion.topics_include.slice(0, 3).join(", ");
    return tr
      ? `Markanın sesiyle örnek gönderi — ${ts}.`
      : `Sample post in your brand's voice — ${ts}.`;
  }
  return tr
    ? "Markanın sesiyle örnek gönderi — sıcak, açık, sahici."
    : "Sample post in your brand's voice — warm, clear, honest.";
}
